import React, { PropsWithChildren } from 'react';
import { makeStyles, Box } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { usePermission } from '@backstage/plugin-permission-react';
import { finopsPagePermission } from '@internal/plugin-permissions-common-common';
import { usePendingApprovals } from '../../hooks/useApprovals';
import { MarketplaceNav } from './MarketplaceNav';
import { STSNav } from './STSNav';
import { FinOpsNav } from './FinOpsNav';
import { N8NNav } from './N8NNav';
import { TabNav } from './TabNav';
import { FinanceNav } from '@internal/backstage-plugin-finance-reporting';
import { useLoginTracking, useExpandableNav } from './hooks';
import { AgentChatTemplateProvider } from './STSNavItem';

type ActiveSection =
  | 'marketplace'
  | 'finops'
  | 'sts'
  | 'n8n'
  | 'creative'
  | 'finance'
  | 'media'
  | 'dx';

const getActiveSection = (pathname: string): ActiveSection => {
  if (pathname.startsWith('/sts')) return 'sts';
  if (pathname.startsWith('/finops')) return 'finops';
  if (pathname.startsWith('/n8n')) return 'n8n';
  if (pathname.startsWith('/creative')) return 'creative';
  if (pathname.startsWith('/finance')) return 'finance';
  if (pathname.startsWith('/media')) return 'media';
  if (pathname.startsWith('/dx')) return 'dx';
  return 'marketplace';
};

const useStyles = makeStyles({
  sidebar: {
    position: 'fixed',
    left: 0,
    top: 80,
    bottom: 0,
    display: 'block',
    padding: '12px 0',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: 1200,
    transition: 'width 0.3s ease-in-out',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  sidebarMarketplace: {
    width: '72px',
    backgroundColor: '#1a2332',
  },
  sidebarSTS: {
    width: '240px',
    backgroundColor: '#ffffff',
    boxShadow: '2px 0 8px rgba(0,0,0,0.12)',
  },
  sidebarFinance: {
    width: '240px',
    backgroundColor: '#ffffff',
    boxShadow: '4px 0 24px rgba(0,0,0,0.06)',
    padding: 0,
  },
  contentMarketplace: {
    marginLeft: '72px',
    height: '100%',
    overflow: 'visible',
    transition: 'margin-left 0.3s ease-in-out',
  },
  contentSTS: {
    marginLeft: '240px',
    height: '100%',
    overflow: 'visible',
    transition: 'margin-left 0.3s ease-in-out',
  },
  contentFullWidth: {
    marginLeft: 0,
    height: '100%',
    overflow: 'visible',
  },
});

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const classes = useStyles();
  const { pendingCount } = usePendingApprovals();
  const location = useLocation();

  useLoginTracking();

  const {
    isExpanded: infrastructureExpanded,
    setIsExpanded: setInfrastructureExpanded,
    isActive: isInfrastructureActive,
  } = useExpandableNav('/sts/infra-provisioning', ['/sts/agent-chat']);
  const {
    isExpanded: operationsExpanded,
    setIsExpanded: setOperationsExpanded,
    isActive: isOperationsActive,
  } = useExpandableNav('/sts/operations', []);

  const activeSection = getActiveSection(location.pathname);

  const { allowed: hasFinOpsAccess } = usePermission({
    permission: finopsPagePermission,
  });
  const isFinOpsDenied = activeSection === 'finops' && !hasFinOpsAccess;

  const isStsPath = location.pathname.startsWith('/sts');
  const showSidebar = !isStsPath && !isFinOpsDenied;
  const isFinanceSidebar = activeSection === 'finance';
  const isCompactSidebar =
    isStsPath ||
    isFinOpsDenied ||
    activeSection === 'marketplace' ||
    activeSection === 'creative' ||
    activeSection === 'media' ||
    activeSection === 'dx';

  return (
    <AgentChatTemplateProvider>
      <>
        {showSidebar && (
          <Box
            className={`${classes.sidebar} ${
              isCompactSidebar
                ? classes.sidebarMarketplace
                : isFinanceSidebar
                ? classes.sidebarFinance
                : classes.sidebarSTS
            }`}
          >
            {activeSection === 'marketplace' ? (
              <MarketplaceNav pendingCount={pendingCount ?? 0} />
            ) : activeSection === 'finops' && hasFinOpsAccess ? (
              <FinOpsNav />
            ) : activeSection === 'n8n' ? (
              <N8NNav />
            ) : activeSection === 'finance' ? (
              <FinanceNav />
            ) : activeSection === 'creative' ||
              activeSection === 'media' ||
              activeSection === 'dx' ? (
              <TabNav tabId={activeSection} />
            ) : (
              <STSNav
                infrastructureExpanded={infrastructureExpanded}
                operationsExpanded={operationsExpanded}
                isInfrastructureActive={isInfrastructureActive}
                isOperationsActive={isOperationsActive}
                setInfrastructureExpanded={setInfrastructureExpanded}
                setOperationsExpanded={setOperationsExpanded}
              />
            )}
          </Box>
        )}
        <Box
          className={
            isStsPath || isFinOpsDenied
              ? classes.contentFullWidth
              : isCompactSidebar
              ? classes.contentMarketplace
              : classes.contentSTS
          }
        >
          {children}
        </Box>
      </>
    </AgentChatTemplateProvider>
  );
};
